<template>
  <section class="request-access-cta">
    <h1 class="request-access-cta serif">Request Access</h1>
    <div class="column alignCentreColumn">
      <small class="request-access-cta description">
        Fill in your details to request access to The Path Universe.
      </small>

      <form class="request-access-cta-form" @submit.prevent="submitRequest">
        <input
          type="text"
          id="requestName"
          v-model="requestForm.name"
          placeholder="Your name"
          class="input-top"
        />
        <input
          type="email"
          id="requestEmail"
          v-model="requestForm.email"
          placeholder="Your email"
          class="input-middle"
        />
        <textarea
          id="requestReason"
          v-model="requestForm.reason"
          placeholder="Tell us why you're interested"
          class="textarea-bottom"
        ></textarea>
        <div class="sendMessage-section">
          <button type="button" class="cancel-btn" @click="cancelRequest">
            Cancel
          </button>
          <button type="submit" :disabled="isSubmitting">
            {{ isSubmitting ? "Sending..." : "Send request" }}
          </button>
        </div>
      </form>
    </div>

    <!-- Feedback Toast for form submission -->
    <FeedbackToast
      :message="requestMessage"
      :variant="requestSuccess ? 'success' : 'error'"
      :show="showFeedbackToast"
      @dismissed="dismissFeedback"
    />
  </section>
</template>

<script>
import { ref, getCurrentInstance } from "vue";
import FeedbackToast from "@/components/GenericComponents/FeedbackToast.vue";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

export default {
  name: "RequestAccessForm",
  components: {
    FeedbackToast,
  },
  emits: ["requestSubmitted", "closeDrawer"],
  setup(_, { emit }) {
    // Request form state
    const requestForm = ref({
      name: "",
      email: "",
      reason: "",
    });
    const isSubmitting = ref(false);
    const requestMessage = ref("");
    const requestSuccess = ref(false);
    const showFeedbackToast = ref(false);

    // Dismiss feedback toast
    function dismissFeedback() {
      showFeedbackToast.value = false;
      requestMessage.value = "";
    }

    /**
     * Handles the cancel button click
     */
    function cancelRequest() {
      emit("closeDrawer");

      // Try to access parent component directly as a fallback
      if (getCurrentInstance()) {
        const instance = getCurrentInstance();
        if (
          instance.parent &&
          instance.parent.exposed &&
          instance.parent.exposed.closeDrawer
        ) {
          instance.parent.exposed.closeDrawer();
        }
      }
    }

    /**
     * Submits the password request form to Firestore
     */
    async function submitRequest() {
      // Validate form
      if (!requestForm.value.name || !requestForm.value.email) {
        requestMessage.value = "Please fill in your name and email.";
        requestSuccess.value = false;
        showFeedbackToast.value = true;
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(requestForm.value.email)) {
        requestMessage.value = "Please enter a valid email address.";
        requestSuccess.value = false;
        showFeedbackToast.value = true;
        return;
      }

      isSubmitting.value = true;
      requestMessage.value = "";

      try {
        const response = await fetch(
          "https://us-central1-the33path.cloudfunctions.net/sendEmailOnRequest",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              name: requestForm.value.name,
              email: requestForm.value.email,
              message: requestForm.value.reason,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();

        // Success
        requestSuccess.value = true;
        requestMessage.value =
          "Your request has been sent successfully. We'll contact you soon.";
        showFeedbackToast.value = true;

        // Close drawer after showing success toast
        setTimeout(() => {
          emit("requestSubmitted");
          // Reset form
          requestForm.value = { name: "", email: "", reason: "" };
        }, 2000);
      } catch (error) {
        requestSuccess.value = false;
        requestMessage.value =
          "There was an error sending your request. Please try again later.";
        showFeedbackToast.value = true;
        // No emit here, so the drawer stays open on error
      } finally {
        isSubmitting.value = false;
      }
    }

    return {
      requestForm,
      submitRequest,
      cancelRequest,
      isSubmitting,
      requestMessage,
      requestSuccess,
      showFeedbackToast,
      dismissFeedback,
    };
  },
};
</script>

<style scoped>
.request-access-cta {
  width: 100%;
  overflow: visible;
  position: relative;
  padding: 0 var(--space);
  text-align: center;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border-bottom: 0px solid transparent !important;
}

.request-access-cta.description {
  margin: calc(var(--clickableHeight)) calc(var(--clickableHeight)) var(--space)
    calc(var(--clickableHeight));
}

.request-access-cta.description-nda {
  margin: 0 calc(var(--clickableHeight)) calc(var(--clickableHeight))
    calc(var(--clickableHeight));
  font-size: 0.9rem;
  opacity: 0.8;
  max-width: 500px;
}

.request-access-cta h1 {
  font-size: 2rem;
  margin-bottom: 20px;
}

.request-access-cta-form {
  min-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 0;
  flex: 1;
  margin-bottom: var(--clickableHeight);
}

.request-access-cta input,
.request-access-cta textarea {
  width: 100%;
  max-width: 500px;
  margin-bottom: 10px;
  padding: 10px;
  font-size: 1rem;
}

.sendMessage-section {
  margin-top: var(--space);
  left: 50%;
  display: flex;
  justify-content: center;
  width: 100%;
  gap: var(--space);
  align-items: center;
  flex-direction: row;
}

.cancel-btn {
  background-color: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-color);
}

.success-message {
  color: var(--green);
  margin-top: var(--space);
}

.error-message {
  color: var(--red);
  margin-top: var(--space);
}

/* Transition for messages */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Mobile responsiveness */
@media (max-width: 480px) {
  .sendMessage-section {
    flex-direction: column;
    gap: var(--space);
  }
}
</style>
